import { formatIfsDocumentLastActionDisplay } from "./dateFormater";

function normalizeWhitespace(value) {
  return String(value ?? "")
    .trim()
    .replace(/\s+/g, " ");
}

function toReadableState(stateValue) {
  const normalized = normalizeWhitespace(stateValue);
  if (!normalized) return "";
  return normalized
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\bChecked In\b/gi, "Checked-in")
    .replace(/\bChecked Out\b/gi, "Checked-out")
    .replace(/\bUndo Check Out\b/gi, "Undo Check-out")
    .replace(/\b[A-Z]/g, (m) => m.toUpperCase());
}

function parseDateCandidate(candidate) {
  if (!candidate) return null;
  if (candidate instanceof Date) {
    return Number.isNaN(candidate.getTime()) ? null : candidate;
  }
  if (typeof candidate === "number") {
    const d = new Date(candidate);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const text = String(candidate).trim();
  if (!text) return null;

  const direct = new Date(text);
  if (!Number.isNaN(direct.getTime())) return direct;

  const dmy = text.match(/^(\d{2})-(\d{2})-(\d{2})$/);
  if (dmy) {
    const day = Number(dmy[1]);
    const month = Number(dmy[2]) - 1;
    const year = 2000 + Number(dmy[3]);
    const parsed = new Date(year, month, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
}

function formatShortDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear());
  return `${year}-${month}-${day}`; 
}

function resolveLatestHistory(latestHistory) {
  if (!latestHistory) return null;
  if (typeof latestHistory === "string") {
    const text = latestHistory.trim();
    try {
      const parsed = JSON.parse(text);
      return resolveLatestHistory(parsed);
    } catch (_error) {
      const getField = (key) => {
        const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const match =
          text.match(new RegExp(`${escaped}\\s*[:=]\\s*"([^"]*)"`, "i")) ||
          text.match(new RegExp(`${escaped}\\s*[:=]\\s*'([^']*)'`, "i")) ||
          text.match(new RegExp(`${escaped}\\s*[:=]\\s*([^,}\\]]+)`, "i"));
        return match?.[1]?.trim() ?? "";
      };

      const parsedFallback = {
        InfoCategory:
          getField("InfoCategory") ||
          getField("infoCategory") ||
          getField("Category") ||
          getField("category"),
        DateCreated: getField("DateCreated") || getField("dateCreated"),
        CreatedBy:
          getField("CreatedBy") ||
          getField("createdBy") ||
          getField("User") ||
          getField("user"),
        CreatedByName:
          getField("CreatedByName") ||
          getField("createdByName") ||
          getField("UserName") ||
          getField("userName"),
      };

      if (
        parsedFallback.InfoCategory ||
        parsedFallback.DateCreated ||
        parsedFallback.CreatedBy ||
        parsedFallback.CreatedByName
      ) {
        return parsedFallback;
      }
      return null;
    }
  }
  if (Array.isArray(latestHistory)) {
    return latestHistory[0] ?? null;
  }
  return typeof latestHistory === "object" ? latestHistory : null;
}

function trimTrailingEmptyDash(display) {
  if (typeof display !== "string") return display;
  return display.replace(/\s*-\s*-\s*$/g, "").trim() || "-";
}

/**
 * Same “Last action” string as the All Documents grid: history line, IFS last-action text, or date + state.
 * @param {Object} [item] - IFS search row, OneDrive working row (with IFS fields), or mixed shape
 * @returns {{ display: string, timestamp: number }}
 */
export function resolveLastAction(item) {
  const history = resolveLatestHistory(
    item?.latestHistory ?? item?.LatestHistory
  );
  const lastAction = item?.lastAction ?? item?.LastAction;
  const lastActionDate = item?.lastActionDate ?? item?.LastActionDate;
  const dtChg = item?.dtChg ?? item?.DtChg;
  const latestHandling = item?.latestHandling ?? item?.LatestHandling;
  const dateCandidates = [
    item?.CurrentlyCheckedOutDate,
    dtChg,
    item?.CheckedOutDate,
    item?.CheckedInDate,
    lastActionDate,
    latestHandling,
    history?.DateCreated,
  ]
    .map(parseDateCandidate)
    .filter(Boolean);

  const latestDate =
    dateCandidates.length > 0
      ? new Date(Math.max(...dateCandidates.map((d) => d.getTime())))
      : null;
  const historyAction =
    toReadableState(history?.InfoCategory) ||
    toReadableState(history?.infoCategory) ||
    toReadableState(history?.Category) ||
    toReadableState(history?.category);

  const state =
    historyAction ||
    toReadableState(item?.EdmDbState) ||
    toReadableState(lastAction) ||
    toReadableState(item?.Objstate) ||
    toReadableState(item?.State) ||
    toReadableState(item?.Status);

  const user = normalizeWhitespace(
    item?.LastActionUser ??
      history?.CreatedBy ??
      history?.createdBy ??
      history?.CreatedByName ??
      history?.createdByName ??
      history?.UserName ??
      history?.User ??
      item?.DocRespName ??
      ""
  );

  const dateText = formatShortDate(latestDate);
  const stateText = state || "-";
  const userText = user ? ` by ${user}` : "";

  if (historyAction) {
    return {
      display: trimTrailingEmptyDash(
        dateText
          ? `${dateText} - ${historyAction}${userText}`
          : `${historyAction}${userText}`.trim()
      ),
      timestamp: latestDate?.getTime() ?? 0,
    };
  }

  const rawLastActionText = normalizeWhitespace(lastAction);
  if (rawLastActionText) {
    const formattedLastAction = formatIfsDocumentLastActionDisplay(rawLastActionText);
    if (formattedLastAction && formattedLastAction !== "-") {
      return {
        display: formattedLastAction,
        timestamp: latestDate?.getTime() ?? 0,
      };
    }
  }

  const display = trimTrailingEmptyDash(
    dateText ? `${dateText} - ${stateText}${userText}` : `${stateText}${userText}`.trim()
  );

  return {
    display: display || "-",
    timestamp: latestDate?.getTime() ?? 0,
  };
}
